import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { useI18n } from "../i18n";
import LanguageToggle from "./LanguageToggle";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type AspectRatio = "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9";

const ASPECT_RATIOS: { value: AspectRatio; label: string }[] = [
  { value: "1:1", label: "1:1" },
  { value: "16:9", label: "16:9" },
  { value: "9:16", label: "9:16" },
  { value: "4:3", label: "4:3" },
  { value: "3:4", label: "3:4" },
  { value: "21:9", label: "21:9" },
];

type ImageModel = "gemini-3.1-flash-image" | "gemini-3-pro-image";

// Labels are product names and stay untranslated; descriptions are translated
// at render time via the descriptionKey.
const IMAGE_MODELS: {
  value: ImageModel;
  label: string;
  descriptionKey: "modelFast" | "modelHighQuality";
}[] = [
  { value: "gemini-3.1-flash-image", label: "Nano Banana 2", descriptionKey: "modelFast" },
  { value: "gemini-3-pro-image", label: "Nano Banana Pro", descriptionKey: "modelHighQuality" },
];

const MAX_REFERENCE_IMAGES = 5;

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState("");
  const [referenceImages, setReferenceImages] = useState<File[]>([]);
  const [referencePreviews, setReferencePreviews] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [aspectRatioEnabled, setAspectRatioEnabled] = useState(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>("1:1");
  const [selectedModel, setSelectedModel] = useState<ImageModel>("gemini-3-pro-image");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createPending = useMutation(api.images.createPending);
  const generateUploadUrl = useMutation(api.storage.generateUploadUrl);

  const addFiles = useCallback((files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    if (imageFiles.length === 0) return;

    setReferenceImages((prev) => {
      const remaining = MAX_REFERENCE_IMAGES - prev.length;
      const toAdd = imageFiles.slice(0, remaining);
      if (toAdd.length === 0) return prev;
      setReferencePreviews((prevPreviews) => [
        ...prevPreviews,
        ...toAdd.map((f) => URL.createObjectURL(f)),
      ]);
      return [...prev, ...toAdd];
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [addFiles]);

  const removeReferenceImage = useCallback((index: number) => {
    setReferencePreviews((prev) => {
      URL.revokeObjectURL(prev[index]!);
      return prev.filter((_, i) => i !== index);
    });
    setReferenceImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearReferenceImages = useCallback(() => {
    referencePreviews.forEach((url) => URL.revokeObjectURL(url));
    setReferenceImages([]);
    setReferencePreviews([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [referencePreviews]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll on mobile when drawer is open
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;

    setIsGenerating(true);

    try {
      let referenceStorageIds: Id<"_storage">[] | undefined;

      // Upload all reference images in parallel
      if (referenceImages.length > 0) {
        const ids = await Promise.all(
          referenceImages.map(async (file) => {
            const uploadUrl = await generateUploadUrl();
            const response = await fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": file.type },
              body: file,
            });
            const { storageId } = await response.json();
            return storageId as Id<"_storage">;
          })
        );
        referenceStorageIds = ids;
      }

      // Create pending image record (this will trigger the action via scheduler)
      await createPending({
        prompt: prompt.trim(),
        referenceStorageIds,
        aspectRatio: aspectRatioEnabled ? selectedAspectRatio : undefined,
        model: selectedModel,
      });

      // Clear form
      setPrompt("");
      clearReferenceImages();

      // Close sidebar on mobile after generating
      onClose();
    } catch (error) {
      console.error("Failed to start generation:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Sidebar - drawer on mobile, fixed on desktop */}
      <aside
        className={`fixed z-50 bg-white shadow-soft flex flex-col transition-transform duration-300 ease-out
          /* Mobile: bottom sheet drawer */
          inset-x-0 bottom-0 h-[85vh] rounded-t-[24px]
          /* Desktop: left sidebar */
          md:left-0 md:top-0 md:bottom-auto md:right-auto md:w-[380px] md:h-screen md:rounded-none md:border-r md:border-linen-200
          ${isOpen ? "translate-y-0" : "translate-y-full md:translate-y-0"}
        `}
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 bg-linen-300 rounded-full" />
        </div>

        {/* Header with close button on mobile */}
        <header className="px-5 py-4 md:p-6 border-b border-linen-200 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-serif text-charcoal tracking-wide">
              {t.sidebar.title}
            </h1>
            <p className="text-xs md:text-sm text-charcoal-muted mt-0.5">
              {t.sidebar.subtitle}
            </p>
          </div>
          <div className="flex flex-col items-end">
            <a
              href="/admin"
              className="flex min-h-[44px] items-center rounded-soft px-2 text-sm text-sage-600 transition-colors hover:text-sage-700 hover:underline whitespace-nowrap"
            >
              {t.sidebar.tryOnLinks} →
            </a>
            <LanguageToggle />
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-linen-100 flex items-center justify-center text-charcoal-muted hover:bg-linen-200 md:hidden"
            aria-label={t.common.close}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 p-5 md:p-6 flex flex-col gap-5 md:gap-6 overflow-y-auto">
          {/* Prompt Input */}
          <div className="flex flex-col gap-2">
            <label htmlFor="prompt" className="text-sm font-medium text-charcoal">
              {t.sidebar.prompt}
            </label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t.sidebar.promptPlaceholder}
              className="w-full h-28 md:h-32 p-4 bg-linen-50 border border-linen-200 rounded-soft resize-none text-charcoal text-base placeholder:text-charcoal-muted focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent"
            />
          </div>

          {/* Reference Image Upload */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-charcoal">
              {t.sidebar.referenceImage}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            {/* Thumbnail row for selected reference images */}
            {referencePreviews.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {referencePreviews.map((preview, index) => (
                  <div key={preview} className="relative flex-shrink-0 w-20 h-20">
                    <img
                      src={preview}
                      alt={t.sidebar.referenceAlt(index + 1)}
                      className="w-full h-full object-cover rounded-soft border border-linen-200"
                    />
                    <button
                      onClick={() => removeReferenceImage(index)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center text-charcoal-muted hover:bg-coral-400 hover:text-white transition-colors shadow-sm border border-linen-200"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Drop zone — shrinks when images present, hidden at max */}
            {referencePreviews.length < MAX_REFERENCE_IMAGES && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full border-2 border-dashed rounded-soft flex flex-col items-center justify-center cursor-pointer transition-colors ${
                  referencePreviews.length > 0 ? "h-20 md:h-24" : "h-32 md:h-40"
                } ${
                  isDragging
                    ? "border-sage-500 bg-sage-400/10"
                    : "border-linen-300 hover:border-sage-400 hover:bg-linen-50"
                }`}
              >
                <svg className="w-6 h-6 text-charcoal-muted mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-xs text-charcoal-muted text-center px-4">
                  {referencePreviews.length > 0
                    ? t.sidebar.addMore(referencePreviews.length, MAX_REFERENCE_IMAGES)
                    : t.sidebar.dropOrBrowse}
                </p>
              </div>
            )}
          </div>

          {/* Aspect Ratio */}
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <button
                type="button"
                onClick={() => setAspectRatioEnabled(!aspectRatioEnabled)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  aspectRatioEnabled
                    ? "bg-sage-500 border-sage-500"
                    : "border-linen-300 hover:border-sage-400"
                }`}
              >
                {aspectRatioEnabled && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <span className="text-sm font-medium text-charcoal uppercase tracking-wide">
                {t.sidebar.aspectRatio}
              </span>
            </label>

            {aspectRatioEnabled && (
              <div className="flex gap-2 flex-wrap">
                {ASPECT_RATIOS.map(({ value, label }) => {
                  const isSelected = selectedAspectRatio === value;
                  // Calculate aspect ratio for the shape preview
                  const parts = value.split(":").map(Number);
                  const w = parts[0] ?? 1;
                  const h = parts[1] ?? 1;
                  const isLandscape = w > h;
                  const isPortrait = h > w;

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSelectedAspectRatio(value)}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all ${
                        isSelected
                          ? "bg-sage-500/20 ring-2 ring-sage-500"
                          : "hover:bg-linen-100"
                      }`}
                    >
                      {/* Shape preview */}
                      <div
                        className={`border-2 rounded-sm transition-colors ${
                          isSelected ? "border-sage-500" : "border-charcoal-muted/40"
                        }`}
                        style={{
                          width: isLandscape ? 28 : isPortrait ? 16 : 22,
                          height: isPortrait ? 28 : isLandscape ? 16 : 22,
                        }}
                      />
                      <span
                        className={`text-xs font-medium transition-colors ${
                          isSelected ? "text-sage-600" : "text-charcoal-muted"
                        }`}
                      >
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Model Selection - hidden until more models available */}
          {IMAGE_MODELS.length > 1 && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-charcoal">
                {t.sidebar.model}
              </label>
              <div className="flex flex-col gap-1.5">
                {IMAGE_MODELS.map(({ value, label, descriptionKey }) => {
                  const isSelected = selectedModel === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSelectedModel(value)}
                      className={`flex items-center justify-between p-2.5 rounded-lg border transition-all text-left ${
                        isSelected
                          ? "border-sage-500 bg-sage-50"
                          : "border-linen-200 hover:border-linen-300 hover:bg-linen-50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected ? "border-sage-500" : "border-linen-300"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-sage-500" />
                          )}
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? "text-sage-700" : "text-charcoal"}`}>
                          {label}
                        </span>
                      </div>
                      <span className="text-xs text-charcoal-muted">{t.sidebar[descriptionKey]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="p-5 md:p-6 border-t border-linen-200">
          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating}
            className="w-full py-3.5 md:py-3 px-4 bg-sage-500 text-white font-medium rounded-soft hover:bg-sage-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t.sidebar.generating}
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t.sidebar.generateImage}
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
